import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import FloatingContactWidget from "./components/FloatingContactWidget";
import Index from "./pages/Index";
import AccueilV2 from "./pages/AccueilV2";
import Resultats from "./pages/Resultats";
import Rencontre from "./pages/Rencontre";
import Merci from "./pages/Merci";
import PortfolioPage from "./pages/Portfolio";
import ArticlesPage from "./pages/Articles";
import ArticlePage from "./pages/Article";
import ServicesPage from "./pages/Services";
import TarifsPage from "./pages/Tarifs";
import SignaturePage from "./pages/Signature";
import AccompagnementPage from "./pages/Accompagnement";
import PlanDeMatchPage from "./pages/PlanDeMatch";
import Reservation15Minutes from "./pages/Reservation15Minutes";
import AccompagnementQuebecPage from "./pages/AccompagnementQuebec";
import AccompagnementMontrealPage from "./pages/AccompagnementMontreal";
import AccompagnementLavalPage from "./pages/AccompagnementLaval";
import AccompagnementRiveSudPage from "./pages/AccompagnementRiveSud";
import AgenceContenuMontrealPage from "./pages/AgenceContenuMontreal";
import BookingConfirmePage from "./pages/BookingConfirme";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
import ReelsPage from "./pages/Reels";
import PodcastsPage from "./pages/Podcasts";
import EvenementsPage from "./pages/Evenements";
import FormationsPage from "./pages/Formations";
import PublicitesPage from "./pages/Publicites";
import CaseStudiesPage from "./pages/CaseStudies";
import NotFound from "./pages/NotFound";
import VideoGratuite from "./pages/VideoGratuite";
import VideoGratuiteMerci from "./pages/VideoGratuiteMerci";
import MerciAppelPlanDeMatch from "./pages/MerciAppelPlanDeMatch";
import MerciPlanDeMatch from "./pages/MerciPlanDeMatch";
import MerciAppelAccompagnement from "./pages/MerciAppelAccompagnement";
import MerciAccompagnement from "./pages/MerciAccompagnement";
import MetaPixelRouteTracker from "./components/MetaPixelRouteTracker";
import OfferView from "./pages/OfferView";
import CookieBanner from "./components/CookieBanner";
import PlanDeMatchPopup from "./components/PlanDeMatchPopup";
import Booking15Modal from "./components/Booking15Modal";


// Admin pages
import AdminLogin from "./pages/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminArticleEditor from "./pages/admin/AdminArticleEditor";
import MigrateImages from "./pages/admin/MigrateImages";
import AdminGalleries from "./pages/admin/AdminGalleries";
import AdminGalleryEditor from "./pages/admin/AdminGalleryEditor";
import AdminOffers from "./pages/admin/AdminOffers";
import AdminOfferEditor from "./pages/admin/AdminOfferEditor";
import GalleriesIndex from "./pages/GalleriesIndex";
import GalleryView from "./pages/GalleryView";
import GallerySelection from "./pages/GallerySelection";

const queryClient = new QueryClient();

const GlobalOverlays = () => {
  const { pathname } = useLocation();
  if (pathname === "/reservation-15-minutes" || pathname === "/resultats") return null;
  if (pathname === "/accueil-v2" || pathname === "/merci") return <CookieBanner />;

  const hideBookingWidget = pathname === "/accompagnement";

  return (
    <>
      {!hideBookingWidget && <FloatingContactWidget />}
      <PlanDeMatchPopup />
      <CookieBanner />
      {!hideBookingWidget && <Booking15Modal />}
    </>
  );
};


const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <MetaPixelRouteTracker />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/accueil-v2" element={<AccueilV2 />} />
            <Route path="/resultats" element={<Resultats />} />
            <Route path="/rencontre" element={<Rencontre />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:slug" element={<ArticlePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/reels" element={<ReelsPage />} />
            <Route path="/podcasts" element={<PodcastsPage />} />
            <Route path="/evenements" element={<EvenementsPage />} />
            <Route path="/formations" element={<FormationsPage />} />
            <Route path="/publicites" element={<PublicitesPage />} />
            <Route path="/tarifs" element={<TarifsPage />} />
            <Route path="/etudes-de-cas" element={<CaseStudiesPage />} />
            <Route path="/signature" element={<SignaturePage />} />
            <Route path="/accompagnement" element={<AccompagnementPage />} />
            <Route path="/plan-de-match" element={<PlanDeMatchPage />} />
            <Route path="/reservation-15-minutes" element={<Reservation15Minutes />} />
            <Route path="/plandematch" element={<Navigate to="/plan-de-match" replace />} />
            <Route path="/PlanDeMatch" element={<Navigate to="/plan-de-match" replace />} />
            <Route path="/plan-match" element={<Navigate to="/plan-de-match" replace />} />
            <Route path="/plan" element={<Navigate to="/plan-de-match" replace />} />


            <Route path="/accompagnement-marketing-quebec" element={<AccompagnementQuebecPage />} />
            <Route path="/accompagnement-marketing-montreal" element={<AccompagnementMontrealPage />} />
            <Route path="/accompagnement-marketing-laval" element={<AccompagnementLavalPage />} />
            <Route path="/accompagnement-marketing-rive-sud" element={<AccompagnementRiveSudPage />} />
            <Route path="/agence-creation-contenu-montreal" element={<AgenceContenuMontrealPage />} />
            <Route path="/booking-confirme" element={<BookingConfirmePage />} />
            <Route path="/galeries" element={<GalleriesIndex />} />
            <Route path="/galerie/:slug" element={<GalleryView />} />
            <Route path="/galerie-selection/:token" element={<GallerySelection />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/videogratuite" element={<VideoGratuite />} />
            <Route path="/videogratuite/merci" element={<VideoGratuiteMerci />} />
            <Route path="/offre/:slug" element={<OfferView />} />

            {/* Pages de merci (noindex) */}
            <Route path="/merci" element={<Merci />} />
            <Route path="/merci/appel-plan-de-match" element={<MerciAppelPlanDeMatch />} />
            <Route path="/merci/plan-de-match" element={<MerciPlanDeMatch />} />
            <Route path="/merci/appel-accompagnement" element={<MerciAppelAccompagnement />} />
            <Route path="/merci/accompagnement" element={<MerciAccompagnement />} />

            {/* 301-style redirects (client-side) for legacy /blog/* URLs */}
            <Route path="/blog" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/pourquoi-le-podcast-est-essentiel-pour-votre-marque" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/les-secrets-dune-captation-evenementielle-reussie" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/formation-en-ligne-les-erreurs-a-eviter" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/:slug" element={<Navigate to="/articles" replace />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/new" element={<AdminArticleEditor />} />
              <Route path="articles/:slug/edit" element={<AdminArticleEditor />} />
              <Route path="migrate-images" element={<MigrateImages />} />
              <Route path="galeries" element={<AdminGalleries />} />
              <Route path="galeries/:slug" element={<AdminGalleryEditor />} />
              <Route path="galleries" element={<Navigate to="/admin/galeries" replace />} />
              <Route path="galleries/:slug" element={<AdminGalleryEditor />} />
              <Route path="photos" element={<Navigate to="/admin/galeries" replace />} />
              <Route path="offres" element={<AdminOffers />} />
              <Route path="offres/nouveau" element={<AdminOfferEditor />} />
              <Route path="offres/:slug" element={<AdminOfferEditor />} />
            </Route>

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <GlobalOverlays />
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
